import { SidebarIcon, XIcon } from "lucide-react"
import type { ReactNode } from "react"
import { useTeam } from "~/hooks/useTeam"
import { EntityIdentity } from "~/components/EntityIdentity"
import type { RouterOutputs } from "~/trpc/react"
import { PriorityIcon } from "../icons/priority"
import { IssueLabel } from "../issueDetailsPage/IssueLabel"
import { Button } from "../ui/button"
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from "../ui/sheet"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from "../ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"

export function IssueOverview({
	teamIssueData,
	open,
	onOpenChange
}: {
	teamIssueData: RouterOutputs["issues"]["getTeamIssues"]
	open?: boolean
	onOpenChange?: (open: boolean) => void
}) {
	const { team, members } = useTeam()

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<Tooltip>
				<TooltipTrigger asChild>
					<SheetTrigger asChild>
						<Button variant="outline" size="icon" aria-label="Issue overview">
							<SidebarIcon className="size-5 text-icons" />
						</Button>
					</SheetTrigger>
				</TooltipTrigger>
				<TooltipContent>Issue Overview</TooltipContent>
			</Tooltip>
			<SheetContent className="w-full max-w-full gap-0 overflow-hidden p-0 sm:w-xl sm:max-w-xl">
				<SheetHeader className="flex shrink-0 flex-row items-center justify-between gap-3 border-b border-stroke px-4 py-3">
					<div className="flex min-w-0 grow flex-row items-center gap-1.5">
						<EntityIdentity
							kind="team"
							name={team?.name ?? "?"}
							image={team?.logo}
							size="sm"
							className="grow"
						/>
					</div>
					<div className="flex shrink-0 flex-row items-center gap-2">
						<SheetTitle className="text-sm font-medium text-text-sub">
							Issues Overview
						</SheetTitle>
						<SheetDescription className="sr-only">
							Overview of team issues grouped by assignee, label, and priority.
						</SheetDescription>
						<SheetClose asChild>
							<Button
								variant="ghost"
								size="icon-md"
								aria-label="Close issue overview"
							>
								<XIcon className="size-4" />
							</Button>
						</SheetClose>
					</div>
				</SheetHeader>
				{team ? (
					<Tabs
						className="flex min-h-0 flex-1 flex-col px-4"
						defaultValue="assignees"
					>
						<TabsList className="my-4" stretch wrap>
							<TabsTrigger value="assignees">Assignees</TabsTrigger>
							<TabsTrigger value="labels">Labels</TabsTrigger>
							<TabsTrigger value="priority">Priority</TabsTrigger>
						</TabsList>
						<TabsContent
							value="assignees"
							className="min-h-0 flex-1 overflow-auto"
						>
							<OverviewTable
								data={Object.entries(
									teamIssueData.issues.reduce(
										(acc, issue) => {
											if (issue.assignee) {
												acc[issue.assignee.id] =
													(acc[issue.assignee.id] ?? 0) + 1
											}
											return acc
										},
										{} as Record<string, number>
									)
								)
									.map(([id, count]) => {
										const member = members?.find((member) => member.id === id)
										if (!member) return null
										return {
											item: (
												<TableCell className="grow">
													<EntityIdentity
														kind="user"
														userId={member.orgMember.userId}
														name={member.orgMember.user.name}
														image={member.orgMember.user.image}
														size="sm"
													/>
												</TableCell>
											) as ReactNode,
											count
										}
									})
									.filter(
										(item): item is { item: ReactNode; count: number } =>
											item !== null
									)}
								emptyText="No issues are currently assigned to any team members"
							/>
						</TabsContent>
						<TabsContent
							value="labels"
							className="min-h-0 flex-1 overflow-auto"
						>
							<OverviewTable
								data={Object.entries(
									teamIssueData.issues.reduce(
										(acc, issue) => {
											const labels = issue.labels
											if (labels.length === 0) {
												acc.none = (acc.none ?? 0) + 1
											} else {
												for (const label of labels) {
													acc[label.id] = (acc[label.id] ?? 0) + 1
												}
											}
											return acc
										},
										{} as Record<string, number>
									)
								).map(([id, count]) => {
									const label =
										id === "none"
											? null
											: teamIssueData.issues
													.flatMap((i) => i.labels)
													.find((l) => l.id === id)
									return {
										item: (
											<TableCell className="flex flex-row gap-2 items-center grow">
												{label ? (
													<IssueLabel label={label} />
												) : (
													<span className="text-text-sub">No label set</span>
												)}
											</TableCell>
										) as ReactNode,
										count
									}
								})}
								emptyText="No issues have been labeled yet"
							/>
						</TabsContent>
						<TabsContent
							value="priority"
							className="min-h-0 flex-1 overflow-auto"
						>
							<OverviewTable
								data={Object.entries(
									teamIssueData.issues.reduce(
										(acc, issue) => {
											const priority = issue.priority ?? "None"
											acc[priority] = (acc[priority] ?? 0) + 1
											return acc
										},
										{} as Record<string, number>
									)
								).map(([priority, count]) => ({
									item: (
										<TableCell className="flex flex-row gap-2 items-center grow">
											<PriorityIcon
												priority={
													priority as "None" | "Low" | "Medium" | "High"
												}
											/>
											<span className="text-text-sub">{priority}</span>
										</TableCell>
									) as ReactNode,
									count
								}))}
								emptyText="No issues have been prioritized yet"
							/>
						</TabsContent>
					</Tabs>
				) : null}
			</SheetContent>
		</Sheet>
	)
}

function OverviewTable({
	data,
	emptyText
}: {
	data: { item: ReactNode; count: number }[]
	emptyText: string
}) {
	if (data.length === 0) {
		return (
			<div className="flex justify-center items-center py-8">
				<span className="text-text-small text-xs">{emptyText}</span>
			</div>
		)
	}

	return (
		<Table>
			<TableHeader>
				<TableRow className="text-text-small hover:bg-inherit align-text-bottom">
					<TableHead className="grow">Item</TableHead>
					<TableHead className="text-right">Issue Count</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{data.map((item, i) => {
					return (
						<TableRow key={i}>
							{item.item}
							<TableCell className="text-right">
								<span className="text-text-sub">{item.count}</span>
							</TableCell>
						</TableRow>
					)
				})}
			</TableBody>
		</Table>
	)
}
